import {
  ArticleData,
  HomepageContent,
  HomepageSection,
  ResourcesSection,
  SectionId,
  ShareFeedbackSection,
  UserContactsSection,
} from '@wix/wix-help-widget-common/homepage';

import {
  BlockDetails,
  BlockId,
  blockNames,
  BlockType,
  homepageResourcesName,
  ShareFeedbackItemsName,
  SolutionInfo,
  SupportType,
  UserContactsItemsName,
} from '../types';

type HomepageSolutionInfoMapper = (section: HomepageSection, index: number) => SolutionInfo;

const shareFeedbackSolutionInfo: HomepageSolutionInfoMapper = (section: ShareFeedbackSection, index) => {
  const blockDetails: BlockDetails[] = [];

  if (section.canShowReportABug) {
    blockDetails.push({
      support_name: ShareFeedbackItemsName.ReportABug,
      support_id: null,
      support_position: 1,
      support_type: SupportType.Button,
    });
  }

  if (section.canShowRequestAFeature) {
    blockDetails.push({
      support_name: ShareFeedbackItemsName.RequestAFeature,
      support_id: null,
      support_position: section.canShowReportABug ? 2 : 1, // if "report a bug" is shown(position = 1), "request a feature" position = 2
      support_type: SupportType.Button,
    });
  }

  return {
    block_id: BlockId.ShareFeedback,
    block_name: blockNames[BlockId.ShareFeedback],
    block_position: index + 1,
    block_type: BlockType.Button,
    block_details: blockDetails,
  };
};

const userContactsSolutionInfo: HomepageSolutionInfoMapper = (section: UserContactsSection, index) => {
  const blockDetails: Omit<BlockDetails, 'support_position'>[] = [
    {
      support_name: UserContactsItemsName.Email,
      support_id: null,
      support_type: SupportType.Link,
    },
  ];

  if (!section.teamView) {
    if (section.phoneNumber) {
      blockDetails.unshift({
        support_name: UserContactsItemsName.PhoneNumber,
        support_id: null,
        support_type: SupportType.Text,
      });
    }

    if (section.timeZone) {
      blockDetails.push({
        support_name: UserContactsItemsName.Timezone,
        support_id: null,
        support_type: SupportType.Text,
      });
    }

    if (section.bookASessionLink) {
      blockDetails.push({
        support_name: UserContactsItemsName.BookASessionLink,
        support_id: null,
        support_type: SupportType.Link,
      });
    }
  }

  return {
    block_id: BlockId.SuccessManager,
    block_name: blockNames[BlockId.SuccessManager],
    block_position: index + 1,
    block_type: BlockType.Button,
    block_details: blockDetails.map((block, blockIndex) => ({
      ...block,
      support_position: blockIndex + 1,
    })),
  };
};

const homepageSectionToSolutionInfo: Record<SectionId, HomepageSolutionInfoMapper> = {
  [SectionId.ContactUs]: (_, index) => ({
    block_id: BlockId.ContactUsButton,
    block_name: blockNames[BlockId.ContactUsButton],
    block_position: index + 1,
    block_type: BlockType.Button,
    block_details: [
      {
        support_id: null,
        support_position: 1,
        support_type: SupportType.Button,
      },
    ],
  }),
  [SectionId.ChatWithUs]: (_, index) => ({
    block_id: BlockId.ChatWithUs,
    block_name: blockNames[BlockId.ChatWithUs],
    block_position: index + 1,
    block_type: BlockType.Link,
    block_details: [
      {
        support_id: null,
        support_position: 1,
        support_type: SupportType.Link,
      },
    ],
  }),
  [SectionId.HirePartner]: (_, index) => ({
    block_id: BlockId.HirePartnerButton,
    block_name: blockNames[BlockId.HirePartnerButton],
    block_position: index + 1,
    block_type: BlockType.Button,
    block_details: [
      {
        support_id: null,
        support_position: 1,
        support_type: SupportType.Button,
      },
    ],
  }),
  [SectionId.Resources]: (section: ResourcesSection, index) => ({
    block_id: BlockId.Resources,
    block_name: blockNames[BlockId.Resources],
    block_position: index + 1,
    block_type: BlockType.Link,
    block_details: section.items?.map((item, i) => ({
      support_name: homepageResourcesName[item.resourceId],
      support_type: SupportType.Link,
      support_id: `${item.resourceId}`,
      support_position: i + 1,
    })),
  }),
  [SectionId.StatusPage]: (_, index) => ({
    block_id: BlockId.StatusPage,
    block_name: blockNames[BlockId.StatusPage],
    block_position: index + 1,
    block_type: BlockType.Link,
    block_details: [
      {
        support_id: null,
        support_position: 1,
        support_type: SupportType.Link,
      },
    ],
  }),
  [SectionId.TermsOfUseAndPrivacyPolicy]: (_, index) => ({
    block_id: BlockId.TermsOfUseAndPrivacyPolicy,
    block_name: blockNames[BlockId.StatusPage],
    block_position: index + 1,
    block_type: BlockType.Link,
    block_details: [
      {
        support_id: null,
        support_position: 1,
        support_type: SupportType.Link,
      },
    ],
  }),
  [SectionId.KeraAssistant]: (_, index) => ({
    block_id: BlockId.KeraAssistant,
    block_name: blockNames[BlockId.KeraAssistant],
    block_position: index + 1,
    block_type: BlockType.Button,
    block_details: [
      {
        support_id: null,
        support_position: 1,
        support_type: SupportType.Link,
      },
    ],
  }),
  [SectionId.ShareFeedback]: shareFeedbackSolutionInfo,
  [SectionId.UserContacts]: userContactsSolutionInfo,
};

export function createHomepageSolutionInfo(homepageContent: HomepageContent | null): SolutionInfo[] {
  if (!homepageContent) {
    return [];
  }

  return homepageContent.sections.map((section, index) =>
    homepageSectionToSolutionInfo[section.sectionId](section, index),
  );
}
